// Opciones de planta y línea para los filtros de las lecturas de máquina
// (sobrellenado y cambios de referencia; ambas páginas usan este hook).
//
// Las líneas son TODAS las activas de las plantas visibles (las del dashboard
// del coordinador, misma key que el C1, casi siempre ya en caché, sin poll)
// unidas con las conectadas de GET /api/coordinador/conectividad. Así una
// línea manual cuyas filas aparecen en la tabla (p. ej. Preparación) también
// se puede filtrar.

import { useQuery } from '@tanstack/react-query';
import { coordinadorDashboardSchema } from 'schemas';
import { apiGet } from '../../../lib/api/client';
import { useConectividadLineas } from '../../../lib/api/ingesta';
import { queryKeys } from '../../../lib/api/queries';
import type { OpcionFiltro } from './FiltroRango';

type LineaOpcion = OpcionFiltro & { plantaId: string };

/** Une líneas conectadas y del alcance (sin duplicados) y arma las plantas. */
export function opcionesFiltro(
  conectadas: { linea_id: string; linea_nombre: string; planta_id: string }[],
  delAlcance: { linea_id: string; linea_nombre: string; planta_id: string }[],
  nombresPlanta: Map<string, string>,
): { plantas: OpcionFiltro[]; lineas: LineaOpcion[] } {
  const porId = new Map<string, LineaOpcion>();
  for (const l of [...delAlcance, ...conectadas]) {
    if (!porId.has(l.linea_id)) {
      porId.set(l.linea_id, { id: l.linea_id, nombre: l.linea_nombre, plantaId: l.planta_id });
    }
  }
  const lineas = [...porId.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  const plantaIds = [...new Set(lineas.map((l) => l.plantaId))];
  return {
    plantas:
      plantaIds.length > 1
        ? plantaIds.map((id) => ({ id, nombre: nombresPlanta.get(id) ?? 'Planta' }))
        : [],
    lineas,
  };
}

export function useOpcionesConectadas(): {
  plantas: OpcionFiltro[];
  lineas: LineaOpcion[];
} {
  const conectividad = useConectividadLineas();

  const dashboard = useQuery({
    // Misma key que useCoordinadorDashboard() (el hash omite planta_id undefined).
    queryKey: queryKeys.coordinadorDashboard({ cadencia: 'hoy' }),
    queryFn: () => apiGet('/api/coordinador/dashboard?cadencia=hoy', coordinadorDashboardSchema),
    staleTime: 5 * 60_000,
  });
  const nombres = new Map(
    (dashboard.data?.scope.plantas_asignadas ?? []).map((p) => [p.id, p.nombre]),
  );

  return opcionesFiltro(conectividad.data ?? [], dashboard.data?.lineas ?? [], nombres);
}
